// src/components/blog/search/ClientSearchResults.tsx

'use client'

import { SearchFilters } from '~/components/blog/search/SearchFilters'
import Image from 'next/image'
import Link from 'next/link'
import { formatForDisplay } from '~/lib/blog/utils/formatting'
import type { EmptyState } from '~/components/blog/search/EmptyState'
import { useSearch } from '~/components/blog/search/SearchContext'
import { formatDate, formatDateTime } from '~/lib/utils/date'
import { getBlogListingImageAlt, getBlogListingImageUrl } from '~/lib/blog/utils/listingImage'

interface SearchResultsProps {
  EmptyState: typeof EmptyState
}

function SearchResultsSkeleton(): JSX.Element {
  return (
    <div className="space-y-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <article key={i} className="relative overflow-hidden rounded-2xl border border-gray-200/60 bg-white p-6 dark:border-gray-800/60 dark:bg-gray-900">
          <div className="relative isolate flex flex-col gap-6 sm:flex-row">
            <div className="relative aspect-[16/9] sm:aspect-[2/1] sm:w-72 sm:shrink-0">
              <div className="absolute inset-0 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
            </div>
            <div className="max-w-xl flex-1 space-y-4">
              <div className="flex items-center gap-x-4">
                <div className="h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
                <div className="h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
              </div>
              <div className="space-y-2">
                <div className="h-6 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
                <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
              </div>
            </div>
          </div>
        </article>
      ))}
    </div>
  )
}

export function ClientSearchResults({ EmptyState }: SearchResultsProps): JSX.Element {
  const { results, isLoading, currentFilters, handleFilterChange } = useSearch()

  return (
    <div className="relative grid gap-8 lg:grid-cols-4">
      {/* Filters */}
      <aside className="lg:col-span-1">
        <SearchFilters
          currentFilters={currentFilters}
          resultCount={results.total}
          onFilterChange={handleFilterChange}
          isLoading={isLoading}
        />
      </aside>

      {/* Results */}
      <div className="lg:col-span-3">
        {isLoading ? (
          <SearchResultsSkeleton />
        ) : results.total === 0 ? (
          <EmptyState query={currentFilters.q} />
        ) : (
          <div className="space-y-6">
            {results.posts.map((post) => (
              <article key={post.slug} className="relative overflow-hidden rounded-2xl border border-gray-200/60 bg-white p-6 dark:border-gray-800/60 dark:bg-gray-900">
                <div className="relative isolate flex flex-col gap-6 sm:flex-row">
                  <div className="relative aspect-[16/9] sm:aspect-[2/1] sm:w-72 sm:shrink-0">
                    <Image
                      src={getBlogListingImageUrl(post)}
                      alt={getBlogListingImageAlt(post)}
                      className="absolute inset-0 h-full w-full rounded-lg object-cover"
                      width={288}
                      height={144}
                    />
                  </div>
                  <div className="max-w-xl flex-1">
                    <div className="flex items-center gap-x-4 text-xs">
                      <time 
                        dateTime={formatDateTime(post.date)} 
                        className="text-gray-500 dark:text-gray-400"
                      >
                        {formatDate(post.date, 'full')}
                      </time>
                      {post.topic && (
                        <Link
                          href={post.topic.href}
                          className="relative z-10 rounded-full bg-gray-50 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                        >
                          {post.topic.title}
                        </Link>
                      )}
                    </div>
                    <div className="group relative">
                      <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-gray-600 dark:text-white dark:group-hover:text-gray-300">
                        <Link href={post.href}>
                          <span className="absolute inset-0" />
                          {post.title}
                        </Link>
                      </h3>
                      <p className="mt-2 line-clamp-3 text-sm leading-6 text-gray-600 dark:text-gray-400">
                        {post.description}
                      </p>
                    </div>
                    <div className="mt-4">
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {post.tags.map((tag) => (
                            <button
                              key={tag}
                              onClick={() => handleFilterChange('tag', tag)}
                              className="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-400/20 dark:hover:bg-gray-700"
                            >
                              {formatForDisplay(tag)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}